import React, { useState, useEffect } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    TextField,
    Typography,
    IconButton,
    Icon,
    Box,
    Grid
} from '@material-ui/core';

/**
 * IbmVolumeDetailsDialog - Dialog for displaying and editing IBM Heidi AI storage volume details
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Function to close the dialog
 * @param {Function} props.onSave - Function to save volume changes
 * @param {Object} props.volume - The volume object to display/edit
 * @param {Object} props.storageConfig - The storage configuration object
 * @param {Object} props.policy - The storage policy object (optional, for showing defaults)
 * @param {boolean} props.isEditable - Whether the volume can be edited
 */
const IbmVolumeDetailsDialog = ({
    open,
    onClose,
    onSave,
    volume,
    storageConfig,
    policy = null,
    isEditable = false
}) => {
    const [editedData, setEditedData] = useState({
        mountPath: '',
        overrides: {
            capacity: '',
            iops: '',
            throughput: ''
        }
    });

    // Initialize form data when volume or dialog opens
    useEffect(() => {
        if (open && volume) {
            const headVolume = storageConfig?.headNodeVolumes?.[0];
            
            setEditedData({
                mountPath: volume.mountPath || '/data',
                overrides: {
                    capacity: volume.overrides?.capacity || headVolume?.overrides?.capacity || '',
                    iops: volume.overrides?.iops || headVolume?.overrides?.iops || '',
                    throughput: volume.overrides?.throughput || headVolume?.overrides?.throughput || ''
                }
            });
        }
    }, [open, volume, storageConfig]);

    const handleSave = () => {
        if (!volume) return;

        const updatedVolume = {
            ...volume,
            mountPath: editedData.mountPath,
            overrides: {
                capacity: editedData.overrides.capacity ? Number(editedData.overrides.capacity) : null,
                iops: editedData.overrides.iops ? Number(editedData.overrides.iops) : null,
                throughput: editedData.overrides.throughput ? Number(editedData.overrides.throughput) : null
            }
        };

        // Also update size for backward compatibility
        if (editedData.overrides.capacity) {
            updatedVolume.size = Number(editedData.overrides.capacity);
        }

        onSave(updatedVolume);
    };

    const handleChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleOverrideChange = (field, value) => {
        setEditedData(prev => ({
            ...prev,
            overrides: {
                ...prev.overrides,
                [field]: value
            }
        }));
    };

    if (!volume) return null;

    const headVolume = storageConfig?.headNodeVolumes?.[0];
    
    // Get policy defaults from policy.ibmParams or headVolume.overrides
    const policyDefaults = {
        capacity: policy?.ibmParams?.capacity || headVolume?.overrides?.capacity || 'Not set',
        iops: policy?.ibmParams?.iops || headVolume?.overrides?.iops || 'Not set',
        throughput: policy?.ibmParams?.throughput || headVolume?.overrides?.throughput || 'Not set'
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">
                        {isEditable ? 'Edit' : 'View'} Volume Details
                    </Typography>
                    <IconButton size="small" onClick={onClose}>
                        <Icon>close</Icon>
                    </IconButton>
                </Box>
            </DialogTitle>

            <DialogContent dividers>
                <Grid container spacing={2}>
                    {/* Storage Configuration Info */}
                    <Grid item xs={12}>
                        <Box mb={2} p={2} bgcolor="grey.100" borderRadius={1}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Storage Configuration
                            </Typography>
                            <Typography variant="body1">
                                {storageConfig?.name || 'Unknown Configuration'}
                            </Typography>
                            {storageConfig?.providerName && (
                                <Typography variant="body2" color="textSecondary">
                                    Provider: {storageConfig.providerName}
                                </Typography>
                            )}
                        </Box>
                    </Grid>

                    {/* Mount Path */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Mount Path"
                            value={editedData.mountPath}
                            onChange={(e) => handleChange('mountPath', e.target.value)}
                            disabled={!isEditable}
                            helperText="Where the volume will be mounted on the node"
                        />
                    </Grid>

                    {/* Capacity Override */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Capacity Override (GB)"
                            type="number"
                            value={editedData.overrides.capacity}
                            onChange={(e) => handleOverrideChange('capacity', e.target.value)}
                            disabled={!isEditable}
                            helperText={`Policy default: ${policyDefaults.capacity}${typeof policyDefaults.capacity === 'number' ? ' GB' : ''}`}
                            InputProps={{
                                inputProps: { min: 10, max: 16000 }
                            }}
                        />
                    </Grid>

                    {/* IOPS Override */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="IOPS Override"
                            type="number"
                            value={editedData.overrides.iops}
                            onChange={(e) => handleOverrideChange('iops', e.target.value)}
                            disabled={!isEditable}
                            helperText={`Policy default: ${policyDefaults.iops}${typeof policyDefaults.iops === 'number' ? ' IOPS' : ''}`}
                            InputProps={{
                                inputProps: { min: 100, max: 96000 }
                            }}
                        />
                    </Grid>

                    {/* Throughput Override */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Throughput Override (MB/s)"
                            type="number"
                            value={editedData.overrides.throughput}
                            onChange={(e) => handleOverrideChange('throughput', e.target.value)}
                            disabled={!isEditable}
                            helperText={`Policy default: ${policyDefaults.throughput}${typeof policyDefaults.throughput === 'number' ? ' MB/s' : ''}`}
                            InputProps={{
                                inputProps: { min: 1, max: 4303 }
                            }}
                        />
                    </Grid>

                    {/* Volume Status */}
                    {volume.status && (
                        <Grid item xs={12}>
                            <Box mt={2} p={2} bgcolor="grey.50" borderRadius={1}>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Volume Status
                                </Typography>
                                <Typography variant="body2" style={{ textTransform: 'capitalize' }}>
                                    {volume.status}
                                </Typography>
                                {volume.volumeId && (
                                    <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: '4px' }}>
                                        Volume ID: {volume.volumeId}
                                    </Typography>
                                )}
                                
                                {/* Status explanation */}
                                <Box mt={1} p={1} bgcolor="info.50" borderRadius={1} border="1px solid #E3F2FD">
                                    <Typography variant="caption" color="textSecondary">
                                        {volume.status === 'pending' && (
                                            <>📝 <strong>Pending:</strong> Volume will be created when cluster is deployed</>
                                        )}
                                        {volume.status === 'provisioning' && (
                                            <>⚙️ <strong>Provisioning:</strong> Volume is being created on IBM Cloud</>
                                        )}
                                        {volume.status === 'attached' && (
                                            <>✅ <strong>Attached:</strong> Volume is mounted and ready to use</>
                                        )}
                                        {volume.status === 'failed' && (
                                            <>❌ <strong>Failed:</strong> Volume provisioning encountered an error</>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    )}
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} color="default">
                    {isEditable ? 'Cancel' : 'Close'}
                </Button>
                {isEditable && (
                    <Button onClick={handleSave} color="primary" variant="contained">
                        Save Changes
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default IbmVolumeDetailsDialog;
